import React from "react";
import SEOLandingPage from "./SEOLandingPage";

const SftpClientAndroid: React.FC = () => (
  <SEOLandingPage
    title="SFTP Client for Android — Transfer Files to Remote Servers | Cosmovex"
    metaDescription="ShellPilot includes a built-in SFTP client for Android. Browse remote directories, upload and download files, and manage server file systems from your phone."
    ogTitle="SFTP Client for Android — ShellPilot"
    h1="SFTP Client for Android"
    intro="Browse your server's file system, upload files from your phone, and download logs or configs — all through a secure SFTP connection in ShellPilot."
    paragraphs={[
      "SFTP is the secure standard for transferring files between machines, and ShellPilot brings it to Android with a clean, mobile-native interface. The same connection profile you use for SSH also works for SFTP — one app, both protocols.",
      "The SFTP file browser shows directory listings with file sizes, timestamps, and permissions. You can navigate up and down the directory tree, open text files for quick viewing, and download files directly to your phone's local storage. For deployments and incident response, being able to pull a log file or config off a server from your phone is genuinely valuable.",
      "Uploading works in both directions. Need to push a config update to a server? Select a file from your phone, choose the destination directory, and ShellPilot transfers it over the authenticated SFTP connection. File permissions on the remote side are preserved, and you can set them manually if needed.",
      "ShellPilot's SFTP and SSH features work together seamlessly. You can start an SSH terminal session, notice a file you need to inspect more closely, switch to the SFTP browser and download it, review it in the built-in viewer, then jump back to the terminal. The app keeps both connections active so you don't lose your session.",
    ]}
    features={[
      "SFTP file browser with directory navigation",
      "Download files from remote server to phone",
      "Upload files from phone to remote server",
      "Text file viewer for quick config/log inspection",
      "File permissions display and modification",
      "Shared connection profiles with SSH",
      "Supports both password and key authentication",
      "Transfer progress indicator for large files",
      "Bookmark frequently accessed directories",
      "Works over Wi-Fi and mobile data",
    ]}
    howToTitle="How to transfer files to a server over SFTP on Android"
    howToSteps={[
      { name: "Install ShellPilot", text: "Download ShellPilot free from Google Play — it includes a built-in SFTP client." },
      { name: "Connect to your server", text: "Use the same host profile as SSH — address, port, username, and password or key." },
      { name: "Browse the file system", text: "Open the SFTP browser to navigate directories and view files, sizes, and permissions." },
      { name: "Upload or download", text: "Download a file to your phone, or upload one from local storage to the server." },
    ]}
    useCases={[
      { title: "Pull a log file off a server", body: "Browse to the log directory, download the file to your phone, and review it in the built-in viewer." },
      { title: "Push a config update", body: "Select a file from your phone, choose the destination directory, and transfer it over SFTP." },
      { title: "Inspect a file mid-session", body: "Notice something in the terminal, switch to SFTP, download and view the file, then jump back." },
    ]}
    faqs={[
      { q: "Does ShellPilot include SFTP?", a: "Yes. ShellPilot has a built-in SFTP client that shares connection profiles with its SSH terminal — one app for both." },
      { q: "Can I upload and download files?", a: "Yes, in both directions. Download server files to your phone's storage and upload local files to any remote directory." },
      { q: "Are file permissions preserved?", a: "Remote file permissions are displayed and preserved on transfer, and you can modify them manually when needed." },
      { q: "Is it secure?", a: "SFTP runs over the encrypted SSH connection with password or private-key authentication." },
    ]}
    related={[
      { label: "SSH Client for Android", path: "/ssh-client-android", tagline: "Full terminal sessions on your phone" },
      { label: "Terminal Emulator for Android", path: "/terminal-emulator-android", tagline: "ANSI colors and real key sequences" },
      { label: "PuTTY for Android", path: "/putty-for-android", tagline: "The mobile PuTTY alternative" },
      { label: "Manage Servers from Your Phone", path: "/manage-server-from-phone", tagline: "Run, monitor, and fix servers anywhere" },
    ]}
    playStoreUrl="https://play.google.com/store/apps/details?id=com.cosmovex.shellpilot"
    icon="/images/shellpilot.png"
    appName="ShellPilot"
    accentColor="#A8720E"
    canonicalPath="/sftp-client-android"
  />
);

export default SftpClientAndroid;
